/**
 * The portrait bottom sheet (r14, plans/portrait-phone.md P6–P8, §Y "Sheet"): its three states and the
 * pure state machine. The state is UI state, not race state: a frame is a pure function of (t, mode, take,
 * LITE rungs, sheet state, tab, scroll), and none of it goes in the deep link.
 *
 *   peek  the reserved peek rect (transport, 3 rows, attribution)
 *   half  the sheet top at 45 % of the stage height (the sheet is about 55 % of it)
 *   full  the sheet top just under the header
 *
 * The shell owns the state (app/sheet.ts: drag and taps on a transparent DOM layer); the view lays out and
 * draws from it (`StyleModule.layout(stage, sheet)`, `StyleContext.sheet`).
 */

export type SheetState = 'peek' | 'half' | 'full';
export type SheetTab = 'tower' | 'feed' | 'results';

export const SHEET_STATES: readonly SheetState[] = ['peek', 'half', 'full'];
/** the half state's sheet height as a fraction of the stage height */
export const SHEET_HALF_FRAC = 0.55;
/** a handle drag of at least this many stage px always moves the sheet one state on */
export const SHEET_DRAG_STEP = 24;

/** The shell's sheet, as the view sees it (`StyleContext.sheet`): read it per frame, call back to change it. */
export interface SheetApi {
  readonly state: SheetState;
  readonly tab: SheetTab;
  /** list scroll in stage px; the view clamps it to its content (`clampScroll`) */
  readonly scroll: number;
  /** changes whenever state, tab or scroll change (the HUD redraw key) */
  readonly key: number;
  setTab(tab: SheetTab): void;
  /** open to a state and a tab (the view opens RESULTS at the flag) */
  open(state: SheetState, tab: SheetTab): void;
  /** the list's scroll limit for the current tab (stage px, ≥ 0) */
  clampScroll(max: number): void;
}

/** Tap on the handle: peek → half → full → peek. */
export function cycleSheet(s: SheetState): SheetState {
  return s === 'peek' ? 'half' : s === 'half' ? 'full' : 'peek';
}

/** The sheet top (stage px) in a state: `peekTop` = the peek rect's y, `headerH` = the header's height. */
export function sheetTopOf(s: SheetState, stageH: number, peekTop: number, headerH: number): number {
  if (s === 'peek') return peekTop;
  if (s === 'full') return headerH;
  return Math.max(headerH, Math.min(peekTop, stageH - Math.round(SHEET_HALF_FRAC * stageH)));
}

/** The sheet top (stage px) in every state, for `snapSheet`. */
export function sheetTops(stageH: number, peekTop: number, headerH: number): Record<SheetState, number> {
  return { peek: peekTop, half: sheetTopOf('half', stageH, peekTop, headerH), full: headerH };
}

/**
 * Where a handle drag ends: the state whose top (`tops`) is nearest to the dragged top (`dy` stage px, up
 * < 0); a drag of at least `SHEET_DRAG_STEP` that would land back where it started moves one state on in
 * its direction.
 */
export function snapSheet(from: SheetState, dy: number, tops: Readonly<Record<SheetState, number>>): SheetState {
  const top = tops[from] + dy;
  let best = from;
  let bestD = Number.POSITIVE_INFINITY;
  for (const s of SHEET_STATES) {
    const d = Math.abs(tops[s] - top);
    if (d < bestD) {
      best = s;
      bestD = d;
    }
  }
  if (best !== from || Math.abs(dy) < SHEET_DRAG_STEP) return best;
  const i = SHEET_STATES.indexOf(from) + (dy < 0 ? 1 : -1);
  return SHEET_STATES[Math.min(Math.max(i, 0), SHEET_STATES.length - 1)] ?? from;
}
